// utilities/authToken.js - Token based login (access token + refresh cookie)
//
// The API returns a short-lived access token at login (response.Token). It is kept in memory
// only - never in localStorage - and sent as "Authorization: Bearer ..." by authInterceptor.js.
// The refresh token lives in an HttpOnly cookie that only the API can read; it is used by
// api/Auth/Refresh to get a new access token (after a page reload, or when the old one expires).
import axios from 'axios';
import { API_BASE_URL } from '../config/apiConfig';

// Separate axios instance: auth calls must not go through our own interceptors
const authAxios = axios.create({ withCredentials: true });

// Non-secret flag: "this login received tokens", so after a page reload we know a refresh is
// worth trying. Servers without token support (signing key not configured) never set it.
const TOKEN_MODE_KEY = 'authTokenMode';
const REFRESH_MARGIN_MS = 60 * 1000;          // refresh when less than 1 minute is left
const REFRESH_RETRY_BACKOFF_MS = 5 * 60 * 1000; // after a failed refresh, wait before retrying

let accessToken = null;
let accessTokenExpiresAt = 0;
let refreshPromise = null;
let lastRefreshFailure = 0;

const safeStorage = {
    get: (key) => { try { return localStorage.getItem(key); } catch { return null; } },
    set: (key, value) => { try { localStorage.setItem(key, value); } catch { /* ignore */ } },
    remove: (key) => { try { localStorage.removeItem(key); } catch { /* ignore */ } }
};

// Called with response.Token from login (GetValidEmployee / GetValidUser) and from refresh
export const setTokenInfo = (token) => {
    if (!token || !token.AccessToken) return;
    accessToken = token.AccessToken;
    accessTokenExpiresAt = token.AccessTokenExpiresAt
        ? new Date(token.AccessTokenExpiresAt).getTime()
        : Date.now() + (token.ExpiresIn || 0) * 1000;
    lastRefreshFailure = 0;
    safeStorage.set(TOKEN_MODE_KEY, '1');
};

export const clearTokens = () => {
    accessToken = null;
    accessTokenExpiresAt = 0;
    refreshPromise = null;
    safeStorage.remove(TOKEN_MODE_KEY);
};

export const isTokenMode = () => !!accessToken || safeStorage.get(TOKEN_MODE_KEY) === '1';

const hasUsableToken = () => !!accessToken && Date.now() < accessTokenExpiresAt - REFRESH_MARGIN_MS;

// One refresh at a time - parallel callers share the same request.
// Resolves to true when a new access token was stored, false otherwise (never throws).
export const refreshAccessToken = () => {
    if (refreshPromise) return refreshPromise;

    refreshPromise = authAxios
        .post(`${API_BASE_URL}/Auth/Refresh`, {})
        .then((response) => {
            const token = response.data?.Data;
            if (token?.AccessToken) {
                setTokenInfo(token);
                return true;
            }
            lastRefreshFailure = Date.now();
            return false;
        })
        .catch(() => {
            lastRefreshFailure = Date.now();
            accessToken = null;
            return false;
        })
        .finally(() => {
            refreshPromise = null;
        });

    return refreshPromise;
};

// Access token for the next request, refreshing first if it is missing / about to expire.
// Returns null when there is no usable token (the request is then sent without one).
export const getAccessToken = async () => {
    if (hasUsableToken()) return accessToken;
    if (!isTokenMode()) return null;
    if (Date.now() - lastRefreshFailure < REFRESH_RETRY_BACKOFF_MS) return accessToken;

    await refreshAccessToken();
    return accessToken;
};

// Ends the session on the server (revokes the refresh token, clears the cookie). Fire and forget.
export const serverLogout = () => {
    const wasTokenMode = isTokenMode();
    clearTokens();
    if (!wasTokenMode) return;
    authAxios.post(`${API_BASE_URL}/Auth/Logout`, {}).catch(() => { /* already logged out locally */ });
};
